import {
  memo,
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, ChevronsUp, Sigma } from "lucide-react";
import { LatexKeyboard, KEYBOARD_WIDTH } from "@ever-tutor/et-ui";

import {
  MAX_USER_MESSAGE_LENGTH,
  type SendResult,
} from "@/features/tutor/hooks/useSendChatMessage";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import {
  CHAT_REFUSED_TOAST,
  NOTIFICATION_STACKS,
  NotificationStackId,
} from "@/constants/notification";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import {
  useMathComposer,
  type ComposerDraft,
} from "@/features/tutor/hooks/useMathComposer";
import { useInputBoxMirror } from "@/features/tutor/hooks/useInputBoxMirror";
import MathTextField from "@/features/tutor/components/MathTextField";
import ScratchpadChip from "@/features/tutor/components/ScratchpadChip";
import {
  selectIsNotepadOpen,
  useNotepadStore,
} from "@/features/notepad/store/useNotepadStore";
import {
  selectChipVisible,
  useScratchpadChipStore,
} from "@/features/tutor/store/useScratchpadChipStore";
import { renderMath } from "@/utils/mathjax";
import { CONTROL_BAR_MIN_HEIGHT } from "@/features/tutor/constants/controlBar";
import { provideDevCommand } from "@/devHooks/devHookConnector";

const DISCONNECTED_HINT = "Not connected yet — your message is still here.";
const TOO_LONG_HINT = "That message is too long to send.";

// The counter appears only once the limit is in sight, so the ordinary case
// keeps the bare bar while an over-long message still says why it will not go.
const COUNTER_VISIBLE_FROM = MAX_USER_MESSAGE_LENGTH * 0.9;

const COUNTER_ID = "chat-input-counter";

const GAP = 16;
const EDGE = 8;

const SEND_ICON_WIDTH = 44;
const SEND_LABEL_WIDTH = 128;
const MORPH = { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };
const SWAP = { duration: 0.12, ease: "linear" as const };

const mathPanelOffset = (el: HTMLDivElement | null) => {
  if (!el || typeof window === "undefined") return undefined;

  const rect = el.getBoundingClientRect();
  const centre = window.innerWidth / 2;
  const rightOfBar = rect.right + GAP + KEYBOARD_WIDTH / 2 - centre;
  const maxOffset = centre - KEYBOARD_WIDTH / 2 - EDGE;

  return { x: Math.max(0, Math.min(rightOfBar, maxOffset)), y: 0 };
};

const barRect = (el: HTMLDivElement | null) => {
  if (!el) return null;
  const { top, left, width, height } = el.getBoundingClientRect();
  return { top, left, width, height };
};

const ChatInputBar = ({
  onSend,
  windowOpen,
  onToggleWindow,
  draft,
}: {
  onSend: (text: string) => Promise<SendResult>;
  windowOpen: boolean;
  onToggleWindow: () => void;
  draft?: MutableRefObject<ComposerDraft | null>;
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const connected = useWebsocketStore((state) => state.wsConnected);

  const submitRef = useRef<() => void>(() => {});
  const math = useMathComposer({
    onSubmit: () => submitRef.current(),
    draft: draft?.current,
  });
  useEffect(() => {
    if (!draft) return;
    draft.current = math.isEmpty
      ? null
      : { doc: math.doc, cursor: math.cursor };
  }, [draft, math.doc, math.cursor, math.isEmpty]);
  useInputBoxMirror({
    setText: math.setText,
    text: math.text,
    isEmpty: math.isEmpty,
  });

  const hasWork = useScratchpadChipStore(selectChipVisible);
  const isNotepadOpen = useNotepadStore(selectIsNotepadOpen);
  const length = math.text.trim().length;
  const tooLong = length > MAX_USER_MESSAGE_LENGTH;
  const submitsWorkAlone = hasWork && math.isEmpty;

  const submit = useCallback(async () => {
    // Sending now awaits a canvas export, so a throw here would otherwise be an
    // unhandled rejection that leaves the field frozen with no explanation.
    let result: SendResult;
    try {
      result = await onSend(math.text);
    } catch {
      result = { sent: false, reason: "refused" };
    }

    if (result.sent) {
      math.reset();
      return;
    }

    // The button pre-empts the other reasons, so a refusal here is the one
    // failure a student can reach with the control enabled. Saying nothing
    // reads as a dead button.
    if (result.reason === "refused") {
      useNotificationStore
        .getState()
        .showNotification(
          CHAT_REFUSED_TOAST.message,
          NotificationType.WARNING,
          CHAT_REFUSED_TOAST.durationMs,
          NOTIFICATION_STACKS[NotificationStackId.TOP_CENTER],
        );
    }
  }, [math, onSend]);
  submitRef.current = submit;

  // Dev builds only: send through the same onSend the button calls, minus the
  // typing. Mounted only while the chat field is, so it exists exactly when a
  // student could send.
  useEffect(() => {
    if (!__DEV_OVERLAY__) return undefined;
    return provideDevCommand("tutor.sendChat", async ({ text = "" }) => {
      const result = await onSend(text);
      return result.sent ? { ok: true } : { ok: false, error: result.reason };
    });
  }, [onSend]);

  return (
    <div data-chat-input-bar className="flex w-full flex-col items-stretch">
      <div
        ref={barRef}
        className={`flex ${CONTROL_BAR_MIN_HEIGHT} items-end gap-2 rounded-2xl bg-white p-2 shadow-[0_8px_30px_rgba(0,0,0,0.28)]`}
      >
        <button
          type="button"
          onClick={onToggleWindow}
          aria-label={windowOpen ? "Close conversation" : "Open conversation"}
          aria-expanded={windowOpen}
          title={windowOpen ? "Close conversation" : "Open conversation"}
          className={`flex h-10 w-11 shrink-0 items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#02734A]/60 ${
            windowOpen
              ? "bg-[#E8F7F0] text-[#16A46F]"
              : "text-[#5B6770] hover:bg-[#F1F3F5]"
          }`}
        >
          <ChevronsUp size={18} />
        </button>

        {!isNotepadOpen && <ScratchpadChip />}

        <div className="min-w-0 flex-1 self-center">
          <MathTextField
            ariaLabel="Type your response"
            describedBy={
              length >= COUNTER_VISIBLE_FROM ? COUNTER_ID : undefined
            }
            placeholder="Type your response..."
            fieldRef={math.fieldRef}
            doc={math.doc}
            cursor={math.cursor}
            selectionAnchor={math.selectionAnchor}
            active={math.isActive}
            onSelectionStart={math.beginSelection}
            onSelectionExtend={math.extendSelection}
            onSelectionEnd={math.endSelection}
            onFocusChange={math.setFocused}
          />
        </div>

        <button
          type="button"
          onClick={math.togglePanel}
          aria-label={
            math.isPanelOpen ? "Close math keyboard" : "Math keyboard"
          }
          aria-expanded={math.isPanelOpen}
          title="Math keyboard"
          className={`flex h-10 w-11 shrink-0 items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#02734A]/60 ${
            math.isPanelOpen
              ? "bg-[#E8F7F0] text-[#16A46F]"
              : "text-[#5B6770] hover:bg-[#F1F3F5]"
          }`}
        >
          <Sigma size={18} />
        </button>

        <motion.button
          type="button"
          onClick={() => void submit()}
          aria-label={
            tooLong
              ? `Send. ${TOO_LONG_HINT}`
              : submitsWorkAlone
                ? "Submit work"
                : "Send"
          }
          title={
            tooLong
              ? TOO_LONG_HINT
              : !connected
                ? DISCONNECTED_HINT
                : submitsWorkAlone
                  ? "Submit work"
                  : "Send"
          }
          disabled={(math.isEmpty && !hasWork) || !connected || tooLong}
          animate={{
            width: submitsWorkAlone ? SEND_LABEL_WIDTH : SEND_ICON_WIDTH,
          }}
          transition={MORPH}
          className="relative flex h-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#02734A] text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#02734A]/60 disabled:bg-[#E4E7EA] disabled:text-[#9AA6AD]"
        >
          <AnimatePresence initial={false} mode="wait">
            {submitsWorkAlone ? (
              <motion.span
                key="label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={SWAP}
                className="whitespace-nowrap font-mulish text-sm font-semibold"
              >
                Submit work
              </motion.span>
            ) : (
              <motion.span
                key="icon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={SWAP}
                className="flex items-center justify-center"
              >
                <ArrowUp size={18} strokeWidth={2.5} />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {length >= COUNTER_VISIBLE_FROM && (
        <p
          id={COUNTER_ID}
          role={tooLong ? "alert" : undefined}
          className={`self-end px-2 pt-1 text-xs font-semibold ${
            tooLong ? "text-[#FFB4B4]" : "text-white/70"
          }`}
        >
          {tooLong ? TOO_LONG_HINT : `${length} / ${MAX_USER_MESSAGE_LENGTH}`}
        </p>
      )}

      {math.isPanelOpen &&
        createPortal(
          <LatexKeyboard
            inputPosition={barRect(barRef.current)}
            initialDragOffset={mathPanelOffset(barRef.current)}
            onKeyPress={math.onKeyPress}
            onClose={math.closePanel}
            renderMath={renderMath}
          />,
          document.body,
        )}
    </div>
  );
};

export default memo(ChatInputBar);
